import { Link, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import ClaimsFlowMain from './ClaimsFlowMain'
import { PrimaryButton } from './ui'
import { usePortalAuth } from '../auth/authContexts'
import { usePortalMfaGate } from '../hooks/usePortalMfaGate'
import { useSyncMfaPhone } from '../hooks/useSyncMfaPhone'
import {
  MfaCallCentreMobilePanel,
  MFA_CALL_CENTRE_DISPLAY,
  MFA_CALL_CENTRE_TEL,
} from './MfaCallCentreMobilePanel'
import PostLoginLoadingScreen from './PostLoginLoadingScreen'

/**
 * Shown when a policy-linked user must complete Auth0 SMS MFA (or fix mobile via call centre)
 * before policy detail / claims. Also used at `/mfa/verify` after policy-link email OTP.
 */
export default function MfaEnrollmentPrompt() {
  const navigate = useNavigate()
  const { loginWithMfaEnrollment } = usePortalAuth()
  const { loading, refresh, mfaSatisfied, smsMfaEnrolled, smsMfaMaskedPhone, mfaFactorEnrolled } =
    usePortalMfaGate()
  const { isSyncing, errorMessage, syncAndChallenge } = useSyncMfaPhone()
  const [localEnrolled, setLocalEnrolled] = useState<boolean | null>(null)
  const [localMasked, setLocalMasked] = useState<string | null>(null)
  const [isChallenging, setIsChallenging] = useState(false)
  const [challengeError, setChallengeError] = useState<string | null>(null)

  useEffect(() => {
    if (!loading && mfaSatisfied) {
      navigate('/dashboard', { replace: true })
    }
  }, [loading, mfaSatisfied, navigate])

  const enrolled = localEnrolled ?? smsMfaEnrolled
  const maskedPhone = localMasked ?? smsMfaMaskedPhone
  const canChallenge = enrolled || mfaFactorEnrolled
  const displayError = challengeError ?? errorMessage

  /** Non-SMS factor path: step-up with existing Auth0 session (no sync). */
  const handleContinueWithMfa = async () => {
    setChallengeError(null)
    setIsChallenging(true)
    try {
      await loginWithMfaEnrollment({ returnTo: '/dashboard' })
      await refresh()
      navigate('/dashboard', { replace: true })
    } catch (err: unknown) {
      const e = err as Error & { apiBody?: { message?: string } }
      setChallengeError(
        e?.apiBody?.message ?? e?.message ?? 'Something went wrong. Please try again.',
      )
    } finally {
      setIsChallenging(false)
    }
  }

  /**
   * Re-sync Entsia phone then Auth0 challenge (enrolled “Text me a code”, missing-mobile
   * / factor-path “I’ve updated my number”).
   * Sync API rejects are absorbed by `syncAndChallenge` into `errorMessage` + enrolled:false;
   * keep the enrolled card in that case so “Text me a code” stays available.
   */
  const handleSyncAndChallenge = async () => {
    setChallengeError(null)
    const wasEnrolled = localEnrolled ?? smsMfaEnrolled
    try {
      const result = await syncAndChallenge()
      if (!result.enrolled) {
        // Phone still missing (soft fail) — leave enrolled UI only if we were not enrolled yet.
        if (!wasEnrolled) {
          setLocalEnrolled(false)
        }
        return
      }
      setLocalEnrolled(true)
      setLocalMasked(result.maskedPhone)
      if (result.challengeSucceeded) {
        await refresh()
        navigate('/dashboard', { replace: true })
      }
    } catch (err: unknown) {
      // Defensive: challenge failures return from the hook; unexpected throws still surface here.
      const e = err as Error & { apiBody?: { message?: string } }
      setChallengeError(
        e?.apiBody?.message ?? e?.message ?? 'Something went wrong. Please try again.',
      )
    }
  }

  if (loading || mfaSatisfied) {
    return <PostLoginLoadingScreen />
  }

  const heading = enrolled
    ? 'Verify with a text message'
    : canChallenge
      ? 'Complete multi-factor authentication'
      : 'Update your mobile number'

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsFlowMain>
        <Link
          to="/dashboard"
          className="font-body text-sm font-semibold text-foreground-brand-solid"
        >
          Back to dashboard
        </Link>

        <h1 className="m-0 font-display text-display-xs font-semibold leading-display-xs text-text-primary-900 md:text-display-sm md:leading-display-sm">
          {heading}
        </h1>

        {!enrolled && !canChallenge ? (
          <MfaCallCentreMobilePanel
            reason="missing"
            errorMessage={displayError}
            isSyncing={isSyncing}
            onUpdatedNumber={() => {
              void handleSyncAndChallenge()
            }}
          />
        ) : (
          <div className="w-full max-w-[768px] rounded-2xl border border-border-primary bg-background-primary px-[20px] py-[24px] shadow-xs">
            {enrolled ? (
              <>
                <p className="m-0 font-body text-sm leading-sm text-text-secondary-700">
                  We will text a one-time code
                  {maskedPhone ? (
                    <>
                      {' '}
                      to <strong>{maskedPhone}</strong>
                    </>
                  ) : (
                    <> to the mobile on your policy</>
                  )}
                  .
                </p>
                <p className="mt-4 m-0 font-body text-sm leading-sm text-text-secondary-700">
                  If this isn&apos;t your number, call{' '}
                  <a className="font-semibold text-foreground-brand-solid" href={MFA_CALL_CENTRE_TEL}>
                    {MFA_CALL_CENTRE_DISPLAY}
                  </a>
                  , then return and select <strong>Text me a code</strong>.
                </p>
              </>
            ) : (
              <>
                <p className="m-0 font-body text-sm leading-sm text-text-secondary-700">
                  Complete multi-factor authentication with the method already on your account.
                </p>
                <p className="mt-4 m-0 font-body text-sm leading-sm text-text-secondary-700">
                  Prefer text messages? Call{' '}
                  <a className="font-semibold text-foreground-brand-solid" href={MFA_CALL_CENTRE_TEL}>
                    {MFA_CALL_CENTRE_DISPLAY}
                  </a>{' '}
                  to update the mobile on your policy, then select{' '}
                  <strong>I’ve updated my number</strong>.
                </p>
              </>
            )}

            {displayError ? (
              <div
                className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-900 font-body text-sm"
                role="alert"
              >
                {displayError}
              </div>
            ) : null}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              {enrolled ? (
                <PrimaryButton
                  type="button"
                  className="w-full sm:w-auto"
                  disabled={isSyncing}
                  onClick={() => {
                    void handleSyncAndChallenge()
                  }}
                >
                  {isSyncing ? 'Checking…' : 'Text me a code'}
                </PrimaryButton>
              ) : (
                <>
                  <PrimaryButton
                    type="button"
                    className="w-full sm:w-auto"
                    disabled={isChallenging || isSyncing}
                    onClick={() => {
                      void handleContinueWithMfa()
                    }}
                  >
                    Continue with MFA
                  </PrimaryButton>
                  <PrimaryButton
                    type="button"
                    className="w-full sm:w-auto"
                    disabled={isChallenging || isSyncing}
                    onClick={() => {
                      void handleSyncAndChallenge()
                    }}
                  >
                    {isSyncing ? 'Checking…' : 'I’ve updated my number'}
                  </PrimaryButton>
                </>
              )}
            </div>
          </div>
        )}
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
